import { useState } from "react";
import Button from "@components/ui/Button";
import Table, { TableHead, TableBody, TableRow, TableHeaderCell, TableCell } from "@components/ui/Table";
import { badgeForStatus, RUN_STATUSES } from "@constants/agentSim";
import RunDetailDrawer from "./RunDetailDrawer";

const selectClass = "px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none";

export default function RunsTab({ hook }) {
  const h = hook;
  const [retrying, setRetrying] = useState(null);
  const clinicName = (id) => h.clinics.find((c) => c.id === id)?.clinic_name || id || "—";

  return (
    <div className="space-y-4">
      {h.error && (
        <div className="flex justify-between bg-red-50 text-red-700 text-sm px-3 py-2 rounded">
          <span>{h.error}</span>
          <button onClick={h.clearError}>✕</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select className={selectClass} aria-label="filter-clinic" value={h.filters.clinicId}
          onChange={(e) => h.setFilter("clinicId", e.target.value)}>
          <option value="">All clinics</option>
          {h.clinics.map((c) => <option key={c.id} value={c.id}>{c.clinic_name}</option>)}
        </select>
        <select className={selectClass} aria-label="filter-status" value={h.filters.status}
          onChange={(e) => h.setFilter("status", e.target.value)}>
          <option value="">All statuses</option>
          {RUN_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" aria-label="only failed" checked={h.filters.onlyFailed}
            onChange={(e) => h.setFilter("onlyFailed", e.target.checked)} />
          Only failed
        </label>
        <Button color="light" disabled={h.loading} onClick={() => h.refresh()}>
          {h.loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Clinic</TableHeaderCell>
            <TableHeaderCell>Purpose</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Passed/Failed</TableHeaderCell>
            <TableHeaderCell>Trigger</TableHeaderCell>
            <TableHeaderCell>Created</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {h.runs.map((r) => {
            const b = badgeForStatus(r.status);
            return (
              <TableRow key={r.id} className="cursor-pointer hover:bg-gray-50" onClick={() => h.openRun(r)}>
                <TableCell>{clinicName(r.clinic_id)}</TableCell>
                <TableCell>{r.purpose}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded ${b.className}`}>{b.label}</span></TableCell>
                <TableCell>{r.scenarios_passed ?? 0} / {r.scenarios_failed ?? 0}{r.is_canary ? " · canary" : ""}</TableCell>
                <TableCell>{r.trigger_type}</TableCell>
                <TableCell>{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button
                    color="light"
                    size="xs"
                    disabled={retrying === r.id}
                    title="Run a fresh live simulation for this clinic & purpose"
                    onClick={async () => {
                      setRetrying(r.id);
                      try {
                        await h.triggerRun({ clinicId: r.clinic_id, purpose: r.purpose });
                      } finally {
                        setRetrying(null);
                      }
                    }}
                  >
                    {retrying === r.id ? "Retrying…" : "Retry"}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {h.runs.length === 0 && (
            <TableRow><TableCell colSpan={7}>No runs.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <RunDetailDrawer open={!!h.selectedRun} onClose={h.closeRun} run={h.selectedRun}
        detail={h.detail} loading={h.detailLoading} />
    </div>
  );
}
